/**
 * Page de gestion des pages personnalisées - Administration
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notify } from '../components/NotificationContainer';
import { confirm } from '../components/ConfirmContainer';
import api, { getMediaUrl } from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import logger from '../utils/logger';

const GestionPages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [customPages, setCustomPages] = useState([]);
  const [systemRoutes, setSystemRoutes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [activeTab, setActiveTab] = useState('custom'); // 'custom' ou 'system'

  // État du formulaire
  const [formData, setFormData] = useState({
    title: '',
    url_path: '',
    content_source: 'editor', // 'file' ou 'editor'
    html_content: '', // Pour l'éditeur visuel
    css_content: '', // Pour le CSS en mode éditeur
    js_content: '', // Pour le JS en mode éditeur
    html_file: null,
    css_file: null,
    js_file: null,
    is_active: true,
    include_navbar: false,
    include_footer: false,
    robots_index: true // Autoriser l'indexation par les moteurs de recherche
  });
  const [urlError, setUrlError] = useState('');
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [showHtmlCode, setShowHtmlCode] = useState(false); // Basculer entre éditeur visuel et code brut

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pagesResp, routesResp] = await Promise.all([
        api.get('/custom-pages/'),
        api.get('/custom-pages/routes/')
      ]);

      // Gérer la pagination : pagesResp.data peut être un objet avec results ou un tableau
      const pagesData = Array.isArray(pagesResp.data)
        ? pagesResp.data
        : pagesResp.data.results || [];

      setCustomPages(pagesData);
      setSystemRoutes(routesResp.data);
    } catch (error) {
      logger.error('Erreur lors du chargement:', error);
      notify.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const checkUrlAvailability = async (urlPath) => {
    if (!urlPath || !urlPath.startsWith('/')) {
      setUrlError('L\'URL doit commencer par /');
      return false;
    }

    setCheckingUrl(true);
    try {
      const response = await api.post('/custom-pages/check_url/', {
        url_path: urlPath,
        id: editingPage?.id
      });

      if (response.data.available) {
        setUrlError('');
        return true;
      } else {
        setUrlError(response.data.message);
        return false;
      }
    } catch (error) {
      setUrlError(error.response?.data?.message || 'Erreur lors de la vérification');
      return false;
    } finally {
      setCheckingUrl(false);
    }
  };

  const handleUrlChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, url_path: value });

    // Vérification en temps réel avec debounce
    if (window.urlCheckTimeout) clearTimeout(window.urlCheckTimeout);
    window.urlCheckTimeout = setTimeout(() => {
      checkUrlAvailability(value);
    }, 500);
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    setFormData({ ...formData, [field]: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérification finale de l'URL
    const urlValid = await checkUrlAvailability(formData.url_path);
    if (!urlValid) {
      notify.error('URL invalide ou non disponible');
      return;
    }

    // Validation selon la source de contenu
    if (formData.content_source === 'file') {
      if (!formData.html_file && !editingPage) {
        notify.error('Le fichier HTML est obligatoire');
        return;
      }
    } else if (formData.content_source === 'editor') {
      if (!formData.html_content && !editingPage) {
        notify.error('Le contenu HTML est obligatoire');
        return;
      }
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('url_path', formData.url_path);
      formDataToSend.append('content_source', formData.content_source);
      formDataToSend.append('is_active', formData.is_active);
      formDataToSend.append('include_navbar', formData.include_navbar);
      formDataToSend.append('include_footer', formData.include_footer);
      formDataToSend.append('robots_index', formData.robots_index);

      // Ajouter le contenu selon la source
      if (formData.content_source === 'editor') {
        formDataToSend.append('html_content', formData.html_content);

        // Convertir le CSS en fichier si présent
        if (formData.css_content) {
          const cssBlob = new Blob([formData.css_content], { type: 'text/css' });
          formDataToSend.append('css_file', cssBlob, 'styles.css');
        }

        // Convertir le JS en fichier si présent
        if (formData.js_content) {
          const jsBlob = new Blob([formData.js_content], { type: 'text/javascript' });
          formDataToSend.append('js_file', jsBlob, 'script.js');
        }
      } else if (formData.content_source === 'file' && formData.html_file) {
        formDataToSend.append('html_file', formData.html_file);

        // Fichiers optionnels
        if (formData.css_file) {
          formDataToSend.append('css_file', formData.css_file);
        }
        if (formData.js_file) {
          formDataToSend.append('js_file', formData.js_file);
        }
      }

      if (editingPage) {
        await api.patch(`/custom-pages/${editingPage.id}/`, formDataToSend);
        notify.success('Page modifiée avec succès');
      } else {
        await api.post('/custom-pages/', formDataToSend);
        notify.success('Page créée avec succès');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = async (page) => {
    // IMPORTANT: La page d'accueil "/" doit être modifiée via l'éditeur dédié
    // pour éviter les conflits entre html_content, navbar_html, et footer_html
    if (page.url_path === '/') {
      notify.info('Redirection vers l\'éditeur dédié pour la page d\'accueil...');
      navigate('/admin/pages/accueil');
      return;
    }

    try {
      // Charger les détails complets de la page pour avoir les infos sur les fichiers
      const response = await api.get(`/custom-pages/${page.id}/`);
      setEditingPage(response.data);
      setFormData({
        title: response.data.title,
        url_path: response.data.url_path,
        content_source: response.data.content_source || 'file',
        html_content: response.data.html_content || '',
        css_content: response.data.css_content || '',
        js_content: response.data.js_content || '',
        html_file: null,
        css_file: null,
        js_file: null,
        is_active: response.data.is_active,
        include_navbar: response.data.include_navbar || false,
        include_footer: response.data.include_footer || false,
        robots_index: response.data.robots_index !== undefined ? response.data.robots_index : true
      });
      setUrlError('');
      setShowModal(true);
    } catch (error) {
      logger.error('Erreur lors du chargement de la page:', error);
      notify.error('Erreur lors du chargement des détails de la page');
    }
  };

  const handleDelete = async (page) => {
    // PROTECTION: Empêcher la suppression de la page d'accueil
    if (page.url_path === '/') {
      notify.error('La page d\'accueil ne peut pas être supprimée. Vous pouvez la désactiver ou la modifier.');
      return;
    }

    const confirmed = await confirm.delete(
      `Voulez-vous vraiment supprimer la page "${page.title}" ? Cette action est irréversible.`,
      'Supprimer la page'
    );

    if (confirmed) {
      try {
        await api.delete(`/custom-pages/${page.id}/`);
        notify.success('Page supprimée avec succès');
        loadData();
      } catch (error) {
        logger.error('Erreur:', error);
        notify.error('Erreur lors de la suppression');
      }
    }
  };

  const toggleActive = async (page) => {
    try {
      await api.patch(`/custom-pages/${page.id}/`, {
        is_active: !page.is_active
      });
      notify.success(`Page ${!page.is_active ? 'activée' : 'désactivée'}`);
      loadData();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la modification');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      url_path: '',
      content_source: 'editor',
      html_content: '',
      css_content: '',
      js_content: '',
      html_file: null,
      css_file: null,
      js_file: null,
      is_active: true,
      include_navbar: false,
      include_footer: false,
      robots_index: true
    });
    setEditingPage(null);
    setUrlError('');
    setShowHtmlCode(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditHomePage = () => {
    navigate('/admin/pages/accueil');
  };

  const handleEditNavbarFooter = () => {
    navigate('/admin/pages/navbar-footer');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des pages</h1>
          <p className="mt-2 text-gray-600">
            Gérez les pages du site et créez des pages personnalisées
          </p>
        </div>

        {/* Onglets */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('custom')}
                className={`${
                  activeTab === 'custom'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Pages personnalisées ({customPages.filter(p => p.url_path !== '/').length})
              </button>
              <button
                onClick={() => setActiveTab('system')}
                className={`${
                  activeTab === 'system'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Pages système ({systemRoutes.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Contenu selon l'onglet */}
        {activeTab === 'custom' ? (
          <div>
            {/* Bouton d'ajout de pages personnalisées */}
            <div className="mb-6 flex items-center justify-between">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Créer une page personnalisée
              </button>

              {/* Bouton discret pour la page d'accueil */}
              <button
                onClick={handleEditHomePage}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Modifier la page d'accueil
              </button>

              {/* Bouton pour modifier la navbar et le footer */}
              <button
                onClick={handleEditNavbarFooter}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                Modifier Navbar & Footer
              </button>
            </div>

            {/* Liste des pages personnalisées */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              {customPages.filter(p => p.url_path !== '/').length === 0 ? (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune page personnalisée</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Créez votre première page personnalisée en cliquant sur le bouton ci-dessus
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Titre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        URL
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Créée par
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customPages.filter(p => p.url_path !== '/').map((page) => (
                      <tr key={page.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{page.title}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a
                            href={page.url_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-900"
                          >
                            {page.url_path}
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{page.created_by_name || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(page.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleActive(page)}
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              page.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {page.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleEdit(page)}
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(page)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          /* Liste des pages système */
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chemin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {systemRoutes.map((route, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{route.path}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{route.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          route.type === 'public'
                            ? 'bg-blue-100 text-blue-800'
                            : route.type === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {route.type}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingPage ? 'Modifier la page' : 'Créer une page personnalisée'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Titre */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Titre de la page *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Chemin URL *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.url_path}
                        onChange={handleUrlChange}
                        placeholder="/ma-page"
                        className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                          urlError ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {checkingUrl && (
                        <p className="mt-1 text-sm text-gray-500">Vérification...</p>
                      )}
                      {urlError && (
                        <p className="mt-1 text-sm text-red-600">{urlError}</p>
                      )}
                      {!urlError && formData.url_path && !checkingUrl && (
                        <p className="mt-1 text-sm text-green-600">URL disponible ✓</p>
                      )}
                    </div>

                    {/* Choix de la source de contenu */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source du contenu *
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="content_source"
                            value="editor"
                            checked={formData.content_source === 'editor'}
                            onChange={(e) => setFormData({ ...formData, content_source: e.target.value })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Éditeur visuel</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="content_source"
                            value="file"
                            checked={formData.content_source === 'file'}
                            onChange={(e) => setFormData({ ...formData, content_source: e.target.value })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Upload fichier HTML</span>
                        </label>
                      </div>
                    </div>

                    {/* Éditeur visuel ou Upload fichier selon le choix */}
                    {formData.content_source === 'editor' ? (
                      <>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Contenu HTML *
                            </label>
                            <button
                              type="button"
                              onClick={() => setShowHtmlCode(!showHtmlCode)}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                              </svg>
                              {showHtmlCode ? 'Mode visuel' : 'Afficher le code'}
                            </button>
                          </div>

                          {showHtmlCode ? (
                            <textarea
                              value={formData.html_content}
                              onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                              rows={20}
                              placeholder="<div>Votre code HTML ici...</div>"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                            />
                          ) : (
                            <ReactQuill
                              theme="snow"
                              value={formData.html_content}
                              onChange={(content) => setFormData({ ...formData, html_content: content })}
                              modules={{
                                toolbar: [
                                  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                  ['bold', 'italic', 'underline', 'strike'],
                                  [{ 'color': [] }, { 'background': [] }],
                                  [{ 'align': [] }],
                                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                  ['blockquote', 'code-block'],
                                  ['link', 'image', 'video'],
                                  ['clean']
                                ]
                              }}
                              formats={[
                                'header',
                                'bold', 'italic', 'underline', 'strike',
                                'color', 'background',
                                'align',
                                'list', 'bullet',
                                'blockquote', 'code-block',
                                'link', 'image', 'video'
                              ]}
                              style={{ height: '400px', marginBottom: '50px' }}
                            />
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {showHtmlCode
                              ? 'Modifiez directement le code HTML'
                              : 'Utilisez l\'éditeur pour créer votre contenu HTML'}
                          </p>
                        </div>

                        {/* Éditeur CSS */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            CSS (optionnel)
                          </label>
                          <textarea
                            value={formData.css_content}
                            onChange={(e) => setFormData({ ...formData, css_content: e.target.value })}
                            rows={10}
                            placeholder="/* Ajoutez votre CSS ici */
.ma-classe {
  color: blue;
}"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Styles CSS pour personnaliser l'apparence de votre page
                          </p>
                        </div>

                        {/* Éditeur JavaScript */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            JavaScript (optionnel)
                          </label>
                          <textarea
                            value={formData.js_content}
                            onChange={(e) => setFormData({ ...formData, js_content: e.target.value })}
                            rows={10}
                            placeholder="// Ajoutez votre JavaScript ici
document.addEventListener('DOMContentLoaded', function() {
  logger.log('Page chargée !');
});"
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Code JavaScript pour ajouter des interactions à votre page
                          </p>
                        </div>
                      </>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Fichier HTML {!editingPage && '*'}
                        </label>
                        {editingPage && editingPage.html_file && (
                          <p className="text-sm text-gray-500 mb-1">
                            Fichier actuel: {editingPage.html_file.split('/').pop()}
                          </p>
                        )}
                        <input
                          type="file"
                          accept=".html"
                          onChange={(e) => handleFileChange(e, 'html_file')}
                          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                        />
                        {editingPage && (
                          <p className="text-xs text-gray-400 mt-1">
                            Laissez vide pour conserver le fichier actuel
                          </p>
                        )}
                      </div>
                    )}

                    {/* Fichier CSS */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fichier CSS (optionnel)
                      </label>
                      {editingPage && editingPage.css_file && (
                        <p className="text-sm text-gray-500 mb-1">
                          Fichier actuel: {editingPage.css_file.split('/').pop()}
                        </p>
                      )}
                      <input
                        type="file"
                        accept=".css"
                        onChange={(e) => handleFileChange(e, 'css_file')}
                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      />
                      {editingPage && (
                        <p className="text-xs text-gray-400 mt-1">
                          Laissez vide pour conserver le fichier actuel
                        </p>
                      )}
                    </div>

                    {/* Fichier JS */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fichier JavaScript (optionnel)
                      </label>
                      {editingPage && editingPage.js_file && (
                        <p className="text-sm text-gray-500 mb-1">
                          Fichier actuel: {editingPage.js_file.split('/').pop()}
                        </p>
                      )}
                      <input
                        type="file"
                        accept=".js"
                        onChange={(e) => handleFileChange(e, 'js_file')}
                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      />
                      {editingPage && (
                        <p className="text-xs text-gray-400 mt-1">
                          Laissez vide pour conserver le fichier actuel
                        </p>
                      )}
                    </div>

                    {/* Actif */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Page active
                      </label>
                    </div>

                    {/* Inclure la navbar */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_navbar}
                        onChange={(e) => setFormData({ ...formData, include_navbar: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Inclure la navbar de la page d'accueil
                      </label>
                    </div>

                    {/* Inclure le footer */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_footer}
                        onChange={(e) => setFormData({ ...formData, include_footer: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Inclure le pied de page
                      </label>
                    </div>

                    {/* Autoriser l'indexation */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.robots_index}
                        onChange={(e) => setFormData({ ...formData, robots_index: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Autoriser l'indexation par les moteurs de recherche (SEO)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={checkingUrl || !!urlError}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {editingPage ? 'Modifier' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionPages;
